import type { ReactNode } from 'react';
import { InputNumber, Switch } from 'antd';

import { Field, Input, InputPassword, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import type { GiaTriO, LoiTruong } from '../nghiep-vu/canh-bao';
import type { NotifyField } from '../types';

/**
 * Một ô dựng từ `NotifyField` (ADR-0102 mục 13): form cấu hình kênh và ô tham số của loại cảnh báo dùng chung. Thêm
 * provider mới thì backend khai trường, giao diện không phải viết thêm form.
 *
 * Ô secret không bao giờ có giá trị cũ: đã lưu thì gợi ý "Đã lưu. Để trống để giữ nguyên".
 */
export function OTruong({
  truong,
  nhan,
  goiY,
  giaTri,
  onChange,
  loi,
  daLuuSecret,
  dichTuyChon,
  phu,
  biChan,
}: {
  truong: NotifyField;
  nhan: string;
  goiY?: string;
  giaTri: GiaTriO;
  onChange: (v: GiaTriO) => void;
  loi?: LoiTruong;
  daLuuSecret?: boolean;
  dichTuyChon?: (gia: string) => string;
  /** Nội dung đặt dưới ô, ví dụ nút "Lấy chat ID" hay danh sách biến của thân webhook. */
  phu?: ReactNode;
  biChan?: boolean;
}) {
  const { t } = useT();
  const goiYHien = truong.secret && daLuuSecret ? t('canhBaoKenh.daLuuSecret') : goiY;
  const batBuoc = truong.required && !(truong.secret && daLuuSecret) && truong.kind !== 'BOOLEAN';
  const loiHien = loi ? t(`canhBaoMa.loiTruong.${loi}` as KhoaDich) : undefined;

  if (truong.kind === 'BOOLEAN') {
    return (
      <div className="space-y-1">
        <label className="flex items-start gap-3">
          <Switch checked={giaTri === true} onChange={(v) => onChange(v)} disabled={biChan} aria-label={nhan} />
          <span className="min-w-0">
            <span className="block text-sm text-content-primary">{nhan}</span>
            {goiY && <span className="block text-xs text-content-muted">{goiY}</span>}
          </span>
        </label>
        {phu}
      </div>
    );
  }

  return (
    <Field label={nhan} required={batBuoc} hint={goiYHien} error={loiHien}>
      {(a11y) => (
        <div className="space-y-2">
          {(() => {
            const chuoi = giaTri == null ? '' : String(giaTri);
            switch (truong.kind) {
              case 'SECRET':
                return truong.key === 'headers' ? (
                  <Textarea
                    {...a11y}
                    className="font-mono text-xs"
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    value={chuoi}
                    disabled={biChan}
                    onChange={(e) => onChange(e.target.value)}
                  />
                ) : (
                  <InputPassword
                    {...a11y}
                    autoComplete="new-password"
                    value={chuoi}
                    disabled={biChan}
                    onChange={(e) => onChange(e.target.value)}
                  />
                );
              case 'NUMBER':
                return (
                  <InputNumber
                    {...a11y}
                    className="w-full sm:w-48"
                    min={truong.min ?? undefined}
                    max={truong.max ?? undefined}
                    value={
                      typeof giaTri === 'number' ? giaTri : giaTri === null || giaTri === '' ? null : Number(giaTri)
                    }
                    disabled={biChan}
                    onChange={(v) => onChange(v ?? null)}
                  />
                );
              case 'SELECT':
                return (
                  <Select<string>
                    {...a11y}
                    className="w-full sm:w-72"
                    value={chuoi || undefined}
                    disabled={biChan}
                    onChange={(v) => onChange(v)}
                    options={(truong.options ?? []).map((o) => ({
                      value: o,
                      label: dichTuyChon ? dichTuyChon(o) : o,
                    }))}
                  />
                );
              case 'EMAIL_LIST':
              case 'TEXTAREA':
              case 'JSON':
                return (
                  <Textarea
                    {...a11y}
                    className={truong.kind === 'JSON' ? 'font-mono text-xs' : undefined}
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    value={chuoi}
                    disabled={biChan}
                    onChange={(e) => onChange(e.target.value)}
                  />
                );
              default:
                return (
                  <Input
                    {...a11y}
                    inputMode={truong.kind === 'URL' ? 'url' : undefined}
                    value={chuoi}
                    disabled={biChan}
                    onChange={(e) => onChange(e.target.value)}
                  />
                );
            }
          })()}
          {phu}
        </div>
      )}
    </Field>
  );
}
